"use client";

import { cn } from "@/lib/utils";
import { AnimatePresence } from "motion/react";
import { User } from "next-auth";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { UnreadCount } from "../unread-count";

export type AvatarSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

const sizeMap: Readonly<Record<AvatarSize, number>> = {
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
  "2xl": 96,
  "3xl": 128,
};

export type AvatarUser = Pick<User, "id" | "image" | "name"> | null;

interface Props {
  readonly user: AvatarUser;
  readonly fallbackName?: string;
  readonly fallbackImage?: string;
  readonly size?: AvatarSize;
  readonly scale?: boolean;
  readonly className?: string;
  readonly onClick?: () => void;
  readonly squircle?: boolean;
  readonly badge?: number | boolean | undefined;
}

const anonymousImage = "/anonymous-dark.svg";

export function UserAvatar(props: Props) {
  const {
    user,
    size = "md",
    scale = false,
    fallbackName = "Anonymous",
    fallbackImage = anonymousImage,
    onClick,
    squircle,
  } = props;

  return (
    <span className="relative">
      <Avatar
        className={cn(
          props.className,
          squircle ? "rounded-lg transition-all duration-300 ease-in-out" : "",
        )}
        style={{
          width: sizeMap[size],
          height: sizeMap[size],
          padding:
            !user?.image || user.image.includes(anonymousImage)
              ? "0.3rem"
              : "0",
        }}
        onClick={onClick}
      >
        <AvatarImage
          src={user?.image ?? fallbackImage}
          alt={user?.name ?? fallbackName}
          width={sizeMap[size]}
          height={sizeMap[size]}
          scale={scale}
          className={
            squircle ? "rounded-lg transition-all duration-300 ease-in-out" : ""
          }
        />

        <AvatarFallback>
          {(user?.name?.length ? user.name : fallbackName)[0].toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <AnimatePresence mode="wait">
        {props.badge ? (
          <UnreadCount
            count={props.badge}
            key="unread-count"
            className={"absolute right-0 top-0 flex"}
            badgeClassName={
              typeof props.badge !== "number" ? "h-3 w-3 p-0" : ""
            }
          />
        ) : null}
      </AnimatePresence>
    </span>
  );
}
